/**
 * THÔNG BÁO ĐẨY — bật / tắt trên thiết bị đang mở (03/10/2026). Luật thuần ở `domain/push.ts`; giao diện
 * (lời mời bật chuông, mục trong menu tài khoản) ở `components/PushNotifications.tsx`.
 *
 * Mỗi thiết bị tự bật MỘT lần: xin quyền thông báo, đăng ký với dịch vụ đẩy của trình duyệt, rồi báo địa
 * chỉ đăng ký cho máy chủ. Từ đó tới giờ nhắc là máy đổ chuông, kể cả khi ứng dụng đang đóng.
 */
import { create } from 'zustand';
import type { PushSubscriptionRequest } from '@halo/shared';
import { errorMessage } from '@/api/client';
import { pushApi } from '@/api/endpoints';
import { applicationServerKey, pushState, sameServerKey, type PushState } from '@/domain/push';
import { isIosDevice } from '@/domain/pwa';
import { isStandaloneDisplay } from './pwa-store';

interface PushStoreState {
  state: PushState;
  busy: boolean;
  /** Đọc lại trạng thái của máy; đang bật thì báo lại đăng ký cho máy chủ. Gọi mỗi lần vào app. */
  refresh(): Promise<void>;
  /** Trả về câu báo cho người dùng khi KHÔNG bật được, hoặc `null` khi đã bật. */
  enable(): Promise<string | null>;
  disable(): Promise<void>;
}

const hasPushApi = (): boolean => 'PushManager' in window && 'Notification' in window;

async function currentRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  // `getRegistration`, không phải `ready`: lúc dev không có service worker, `ready` sẽ treo mãi.
  return (await navigator.serviceWorker.getRegistration().catch(() => undefined)) ?? null;
}

async function readDevice() {
  const registration = await currentRegistration();
  const subscription =
    registration && hasPushApi()
      ? await registration.pushManager.getSubscription().catch(() => null)
      : null;
  const state = pushState({
    hasWorker: registration !== null,
    hasPushApi: hasPushApi(),
    permission: hasPushApi() ? Notification.permission : 'default',
    subscribed: subscription !== null,
    ios: isIosDevice(navigator.userAgent, navigator.maxTouchPoints),
    standalone: isStandaloneDisplay(),
  });
  return { registration, subscription, state };
}

/** Thứ máy chủ cần để gửi tin cho thiết bị này; `null` khi trình duyệt trả về đăng ký thiếu khoá. */
function toRequest(subscription: PushSubscription): PushSubscriptionRequest | null {
  const { endpoint, keys } = subscription.toJSON();
  if (!endpoint || !keys?.p256dh || !keys.auth) return null;
  return { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } };
}

export const usePushStore = create<PushStoreState>((set, get) => ({
  state: 'unsupported',
  busy: false,

  async refresh() {
    const { subscription, state } = await readDevice();
    set({ state });
    // Báo lại đăng ký đang có: người khác vừa đăng nhập trên máy này thì máy chủ chuyển nó sang đúng
    // người; máy chủ lỡ mất sổ thì có lại. Lỗi ở đây không đáng làm phiền người dùng.
    const request = state === 'on' && subscription ? toRequest(subscription) : null;
    if (request) await pushApi.subscribe(request).catch(() => undefined);
  },

  async enable() {
    if (!hasPushApi()) return 'Trình duyệt này không nhận được thông báo';
    // Xin quyền TRƯỚC mọi lệnh chờ khác: Safari chỉ mở hộp xin quyền khi còn «dấu» của cú bấm.
    const asking =
      Notification.permission === 'granted'
        ? Promise.resolve<NotificationPermission>('granted')
        : Notification.requestPermission();
    set({ busy: true });
    try {
      const permission = await asking;
      if (permission !== 'granted') {
        await get().refresh();
        return permission === 'denied'
          ? 'Thông báo đang bị chặn — bật lại trong cài đặt của trình duyệt rồi thử lại'
          : 'Chưa cấp quyền thông báo';
      }
      const registration = await currentRegistration();
      if (!registration) return 'Máy này chưa sẵn sàng nhận thông báo — tải lại trang rồi thử lại';
      const { publicKey } = await pushApi.config();
      let subscription = await registration.pushManager.getSubscription();
      if (subscription && !sameServerKey(subscription.options.applicationServerKey, publicKey)) {
        await subscription.unsubscribe();
        subscription = null;
      }
      subscription ??= await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(publicKey),
      });
      const request = toRequest(subscription);
      if (!request) return 'Trình duyệt trả về đăng ký thiếu khoá — thử lại';
      await pushApi.subscribe(request);
      set({ state: 'on' });
      return null;
    } catch (error) {
      await get().refresh();
      return errorMessage(error);
    } finally {
      set({ busy: false });
    }
  },

  async disable() {
    set({ busy: true });
    try {
      const { subscription } = await readDevice();
      if (subscription) {
        // Máy chủ trước, trình duyệt sau: lỡ đứt giữa chừng thì máy chủ không còn gửi vào chỗ trống.
        await pushApi.unsubscribe({ endpoint: subscription.endpoint }).catch(() => undefined);
        await subscription.unsubscribe().catch(() => undefined);
      }
    } finally {
      set({ busy: false });
      await get().refresh();
    }
  },
}));
